import type { Identifier } from "ra-core";

/**
 * The commission ledger: 20 EUR per sale that met the rule (the lead was the
 * seller's and they really talked to the client in the 30 days before),
 * 5 EUR per maintenance closed on the welcome call, and hand-made
 * adjustments. Admins approve, pay or void each line.
 */
export type CommissionKind = "sale" | "maintenance" | "adjustment";
export type CommissionStatus = "pending" | "approved" | "paid" | "void";

export interface Commission {
  id: Identifier;
  sales_id: Identifier;
  kind: CommissionKind;
  amount: number;
  order_id: Identifier | null;
  welcome_call_id: Identifier | null;
  client_id: Identifier | null;
  contact_id: Identifier | null;
  day: string;
  qualified: boolean;
  reason: string | null;
  status: CommissionStatus;
  paid_at: string | null;
  note: string | null;
  created_at: string;
  // From the view
  sales_first_name: string | null;
  first_name: string | null;
  last_name: string | null;
  month: string;
}

export const KIND_LABEL: Record<CommissionKind, string> = {
  sale: "Venta",
  maintenance: "Mantenimiento",
  adjustment: "Ajuste",
};

export const STATUS_LABEL: Record<CommissionStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobada",
  paid: "Pagada",
  void: "Anulada",
};

/** Counts toward what is owed: approved or paid, or pending and within the rule. */
export const counts = (
  commission: Pick<Commission, "status" | "qualified">,
): boolean =>
  commission.status === "approved" ||
  commission.status === "paid" ||
  (commission.status === "pending" && commission.qualified);

export interface SetterTotals {
  sales_id: Identifier;
  name: string;
  /** Owed and not yet paid (approved, or pending within the rule) */
  due: number;
  paid: number;
  /** Pending lines that did not meet the rule: the admin decides */
  doubtful: number;
  lines: Commission[];
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** One block per setter with what is owed, what was paid and the open doubts. */
export const totalsBySetter = (rows: Commission[]): SetterTotals[] => {
  const bySetter = new Map<string, SetterTotals>();
  for (const row of rows) {
    const key = String(row.sales_id);
    const block = bySetter.get(key) ?? {
      sales_id: row.sales_id,
      name: row.sales_first_name ?? "Sin nombre",
      due: 0,
      paid: 0,
      doubtful: 0,
      lines: [],
    };
    const amount = Number(row.amount);
    const next = {
      ...block,
      lines: [...block.lines, row],
      paid: row.status === "paid" ? round2(block.paid + amount) : block.paid,
      due:
        row.status === "approved" || (row.status === "pending" && row.qualified)
          ? round2(block.due + amount)
          : block.due,
      doubtful:
        row.status === "pending" && !row.qualified
          ? block.doubtful + 1
          : block.doubtful,
    };
    bySetter.set(key, next);
  }
  return [...bySetter.values()].sort(
    (a, b) => b.due + b.paid - (a.due + a.paid) || a.name.localeCompare(b.name),
  );
};

/** Euros of commission charged to each sale (for the profit per client). */
export const commissionsByOrder = (
  rows: Pick<Commission, "order_id" | "amount" | "status" | "qualified">[],
): Map<string, number> => {
  const byOrder = new Map<string, number>();
  for (const row of rows) {
    if (row.order_id == null || !counts(row)) continue;
    const key = String(row.order_id);
    byOrder.set(key, round2((byOrder.get(key) ?? 0) + Number(row.amount)));
  }
  return byOrder;
};

/** The label of a line: who it was about and why. */
export const lineLabel = (row: Commission): string => {
  const client = [row.first_name, row.last_name].filter(Boolean).join(" ");
  if (row.kind === "adjustment") return row.note || "Ajuste";
  return client || KIND_LABEL[row.kind];
};

/** YYYY-MM-01 of a date in Madrid. */
export const monthOf = (date: Date): string =>
  `${new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
  }).format(date)}-01`;

export const shiftMonth = (month: string, delta: number): string => {
  const date = new Date(`${month}T12:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + delta);
  return `${date.toISOString().slice(0, 7)}-01`;
};

export const monthLabel = (month: string): string => {
  const text = new Date(`${month}T12:00:00Z`).toLocaleDateString("es-ES", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const parseAmount = (text: string): number | null => {
  const value = Number(text.replace(",", "."));
  return Number.isFinite(value) && value !== 0 ? round2(value) : null;
};
